import { useCallback, useMemo } from "react";
import { useLoginContext } from "../context/loginContextProvider";
import { AccountSettings, DBAccount, DBPosition } from "./types";

const getURL = () => {
  // Use environment override if provided (CRA: REACT_APP_API_URL; Vite: VITE_API_URL)
  const envURL = process.env.REACT_APP_API_URL || process.env.VITE_API_URL;
  if (envURL && envURL.trim().length > 0) {
    return envURL.trim();
  }

  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:8085";
  }

  return "https://api.cryptobot.codeweb.nl";
};

export type APIResponse<T> =
  | {
      success: true;
      data: T;
    }
  | {
      success: false;
      error: string;
    };

export type ParsedLog = {
  errors: number;
  warinings: number;
  errorLines: number[];
  warningLines: number[];
};

export type LogSummary = {
  logGroup: string;
  archiveLogs: string[];
  summary: ParsedLog | undefined;
};

export type LogsContent = {
  summary: ParsedLog;
  content: string;
};

export type SettingsAccount = {
  walletAddress: string;
  apiSecret: string;
  settingsCsv: string;
  executeCsv: string;
  name: string;
};

export type SettingsAccounts = {
  [fileName: string]: SettingsAccount;
};

/**
 * One selectable Propr account. Propr has no `GET /accounts/{id}` endpoint, so
 * this is derived from its challenge-attempts. Propr's "archived" flag is
 * dashboard-only and is NOT exposed by the API, so it cannot be shown.
 */
/** Where an account came from. Funded accounts trade REAL capital. */
export type ProprAccountSource = "challenge" | "funded";

/**
 * Challenge attempts use active|passed|failed; funded accounts use their own
 * active|closed|review_pending.
 */
export type ProprAccountStatus =
  "active" | "passed" | "failed" | "closed" | "review_pending";

/** Statuses an account can never come back from — never selectable. */
export const TERMINAL_PROPR_STATUSES: ProprAccountStatus[] = [
  "failed",
  "closed",
];

export type ProprAccountOption = {
  accountId: string;
  source: ProprAccountSource;
  slug: string;
  size: string;
  accountType: string;
  createdAt: string;
  /** `failed` / `closed` accounts can no longer be traded. */
  status: ProprAccountStatus;
  balance: string;
  currency: string;
  isBeta: boolean;
};

export type Exchange = "hyperliquid" | "okx" | "propr";

export type CreateAccountBody = {
  exchange: Exchange;
  secretFromCsv?: boolean;
  idName: string;
  displayName: string;
  walletAddress: string;
  apiSecret: string;
  passphrase?: string;
  /** Required for `propr`: which `urn:prp-account:...` to trade on. */
  proprAccountId?: string;
  settingsCsv: string;
  executeCsv: string;
  settings: Partial<AccountSettings>;
};

const standardFetch = async (
  endpointURL: string,
  headers: any,
  body?: any,
  method = "GET",
) => {
  const response = await fetch(getURL() + endpointURL, {
    body: body ? JSON.stringify(body) : undefined,
    method,
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
  }).then((res) => res.json());

  return response;
};

export const useAPI = (): {
  checkAuthentication: (
    username: string,
    password: string,
  ) => Promise<boolean | undefined>;
  getAccounts: () => Promise<APIResponse<DBAccount[]>>;
  getPositions: (accountId: string) => Promise<APIResponse<DBPosition[]>>;
  getCsvTrackerPositions: (
    algorithm: string,
    startFrom?: string,
  ) => Promise<APIResponse<any>>;
  getSimulatedPositions: () => Promise<APIResponse<any>>;
  getLogsOverview: () => Promise<APIResponse<LogSummary[]>>;
  getLogsContent: (fileName: string) => Promise<APIResponse<LogsContent>>;
  getSettingsAccounts: () => Promise<APIResponse<SettingsAccounts>>;
  addAccount: (body: CreateAccountBody) => Promise<APIResponse<undefined>>;
  disableAccount: (
    accountId: string,
    archive?: boolean,
    active?: boolean,
  ) => Promise<APIResponse<undefined>>;
  setAccountOwner: (
    accountId: string,
    owner: string | null,
  ) => Promise<APIResponse<undefined>>;
  getProprAccounts: (
    accountId: string,
  ) => Promise<APIResponse<ProprAccountOption[]>>;
  setProprAccountId: (
    accountId: string,
    proprAccountId: string,
  ) => Promise<APIResponse<{ proprAccountId: string }>>;
  getProprAccountsForKey: (
    apiKey: string,
  ) => Promise<APIResponse<ProprAccountOption[]>>;
} => {
  const { username, password } = useLoginContext();
  const headers = useMemo(
    () => ({
      "db-username": username,
      "db-password": password,
    }),
    [username, password],
  );

  const checkAuthentication = useCallback(
    async (username: string, password: string) => {
      try {
        const res = await standardFetch(`/database/accounts`, {
          "db-username": username,
          "db-password": password,
        });
        return res.success;
      } catch (error) {
        console.error("Error checking authentication:", error);
        return false;
      }
    },
    [],
  );

  const getAccounts = useCallback(async () => {
    console.log("🚀 ~ useAPI ~ headers:", headers);
    return await standardFetch(`/database/accounts`, headers);
  }, [headers]);

  const getPositions = useCallback(
    async (accountId: string) => {
      return await standardFetch(
        `/database/positions/by-account?accountId=${accountId}`,
        headers,
      );
    },
    [headers],
  );

  const getCsvTrackerPositions = useCallback(
    async (algorithm: string, startFrom?: string) => {
      const params = new URLSearchParams({ algorithm });
      if (startFrom) {
        params.append("startFrom", startFrom);
      }
      return await standardFetch(
        `/database/csv-tracker/positions-by-algorithm?${params.toString()}`,
        headers,
      );
    },
    [headers],
  );

  const getSimulatedPositions = useCallback(async () => {
    return await standardFetch(`/algorithm/simulations`, headers);
  }, [headers]);

  const getLogsOverview = useCallback(async () => {
    return await standardFetch(`/logs/overview`, headers);
  }, [headers]);

  const getLogsContent = useCallback(
    async (fileName: string) => {
      const paramsStr = new URLSearchParams({
        file: fileName,
      });
      return await standardFetch(
        `/logs/content?${paramsStr.toString()}`,
        headers,
      );
    },
    [headers],
  );

  const getSettingsAccounts = useCallback(async () => {
    console.log("🚀 ~ useAPI ~ headers:", headers);
    return await standardFetch(`/account/list-setting-accounts`, headers);
  }, [headers]);

  const addAccount = useCallback(
    async (body: CreateAccountBody) => {
      return await standardFetch(`/account/add-account`, headers, body, "POST");
    },
    [headers],
  );

  const disableAccount = useCallback(
    async (accountId: string, archived?: boolean, active?: boolean) => {
      const body: any = { accountId };
      if (archived !== undefined) body.archived = archived;
      if (active !== undefined) body.active = active;

      return await standardFetch(
        `/account/disable-account`,
        headers,
        body,
        "PATCH",
      );
    },
    [headers],
  );

  const setAccountOwner = useCallback(
    async (accountId: string, owner: string | null) => {
      return await standardFetch(
        `/account/account-owner`,
        headers,
        { accountId, owner },
        "PATCH",
      );
    },
    [headers],
  );

  // Lists the accounts a raw API key can reach, for creating a new Propr bot
  // account (no DB account exists yet, so it cannot go through
  // getProprAccounts). POST keeps the key out of the URL.
  const getProprAccountsForKey = useCallback(
    async (apiKey: string) => {
      return await standardFetch(
        `/account/propr-accounts-for-key`,
        headers,
        { apiKey },
        "POST",
      );
    },
    [headers],
  );

  const getProprAccounts = useCallback(
    async (accountId: string) => {
      const params = new URLSearchParams({ accountId });
      return await standardFetch(
        `/account/propr-accounts?${params.toString()}`,
        headers,
      );
    },
    [headers],
  );

  const setProprAccountId = useCallback(
    async (accountId: string, proprAccountId: string) => {
      return await standardFetch(
        `/account/propr-account-id`,
        headers,
        { accountId, proprAccountId },
        "PATCH",
      );
    },
    [headers],
  );

  return {
    checkAuthentication,
    getAccounts,
    getPositions,
    getCsvTrackerPositions,
    getSimulatedPositions,
    getLogsOverview,
    getLogsContent,
    getSettingsAccounts,
    addAccount,
    disableAccount,
    setAccountOwner,
    getProprAccounts,
    getProprAccountsForKey,
    setProprAccountId,
  };
};
